import type { UiNode } from '@ory/client';
import { useEffect, useRef } from 'react';

type Props = {
  node: UiNode;
};

/**
 * Scriptノードコンポーネント
 *
 * Ory KratosのServer Driven UIで返されるscriptノードをレンダリングします。
 * 主にWebAuthnなどの認証に必要なスクリプトを読み込むために使用されます。
 *
 * @see https://www.ory.com/docs/kratos/concepts/ui-user-interface#ui-script-nodes
 */
export function ScriptNode({ node }: Props) {
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    // scriptノードでない場合は何もしない
    if (node.type !== 'script') {
      return;
    }

    // 既に読み込まれている場合は何もしない
    if (loadedRef.current) {
      return;
    }

    const attrs = node.attributes as {
      src?: string;
      async?: boolean;
      referrerpolicy?: string;
      crossorigin?: string;
      integrity?: string;
      type?: string;
      id?: string;
      node_type?: string;
    };

    // srcが存在しない場合は何もしない
    if (!attrs.src) {
      return;
    }

    // 既に同じIDのスクリプトが存在するかチェック
    if (attrs.id) {
      const existingScript = document.getElementById(attrs.id);
      if (existingScript) {
        loadedRef.current = true;
        return;
      }
    }

    // スクリプトタグを作成
    const script = document.createElement('script');
    script.src = attrs.src;

    if (attrs.async !== undefined) {
      script.async = attrs.async;
    }

    if (attrs.referrerpolicy) {
      script.referrerPolicy = attrs.referrerpolicy as ReferrerPolicy;
    }

    if (attrs.crossorigin) {
      script.crossOrigin = attrs.crossorigin;
    }

    if (attrs.integrity) {
      script.integrity = attrs.integrity;
    }

    if (attrs.type) {
      script.type = attrs.type;
    }

    if (attrs.id) {
      script.id = attrs.id;
    }

    // スクリプトの読み込み完了を処理
    script.onload = () => {
      loadedRef.current = true;
    };

    script.onerror = () => {
      console.error(`Failed to load script: ${attrs.src}`);
    };

    // DOMに追加
    document.head.appendChild(script);
    scriptRef.current = script;

    // クリーンアップ
    return () => {
      if (scriptRef.current && scriptRef.current.parentNode) {
        scriptRef.current.parentNode.removeChild(scriptRef.current);
        scriptRef.current = null;
        loadedRef.current = false;
      }
    };
  }, [node]);

  return null;
}
